import { useTranslation } from 'react-i18next';
import type { AnalyticsProblemEntryDto, AnalyticsProblemsDto } from '../../api/analytics';

interface ProblemsPanelProps {
  problems: AnalyticsProblemsDto | undefined;
  onSelectScanner: (scanner: string) => void;
  onSelectFinding: (finding: AnalyticsProblemEntryDto) => void;
}

const SEVERITY_DOT: Record<string, string> = {
  Critical: 'bg-red-500',
  Error: 'bg-red-500',
  Warning: 'bg-amber-500',
  Info: 'bg-sky-500',
};

const SEVERITY_BADGE: Record<string, string> = {
  Critical: 'bg-red-100 text-red-700',
  Error: 'bg-red-100 text-red-700',
  Warning: 'bg-amber-100 text-amber-700',
  Info: 'bg-sky-100 text-sky-700',
};

const severityRank = (s: string | null | undefined) =>
  s === 'Critical' ? 3 : s === 'Error' ? 2 : s === 'Warning' ? 1 : 0;

/**
 * AI-detected problems: persisted scanner findings from the decision log,
 * grouped by scanner (newest run only — counts don't inflate on refresh).
 * Each group and each finding is a drill entry point — the "highlight the
 * problems, then dig in" surface.
 */
export default function ProblemsPanel({ problems, onSelectScanner, onSelectFinding }: ProblemsPanelProps) {
  const { t } = useTranslation();

  if (!problems) return null;

  const latestSorted = [...(problems.latest ?? [])]
    .sort((a, b) => severityRank(b.severity) - severityRank(a.severity)
      || new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-700">{t('analytics.problems.title')}</h3>
        {problems.lastRunUtc && (
          <span className="text-xs text-gray-400">
            {t('analytics.problems.lastRun')}: {new Date(problems.lastRunUtc).toLocaleString('lt-LT')}
          </span>
        )}
      </div>

      {problems.totalFindings === 0 ? (
        <p className="text-sm text-gray-400 py-4">{t('analytics.problems.empty')}</p>
      ) : (
        <>
          {/* Scanner groups */}
          <div className="flex flex-wrap gap-2 mb-3">
            {problems.groups.map(group => (
              <button
                key={group.scanner}
                onClick={() => onSelectScanner(group.scanner)}
                className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-full text-sm hover:border-primary-300 transition-colors"
                title={group.latestTitle ?? group.scanner}
              >
                <span className="font-medium text-gray-700">{group.scanner}</span>
                <span className={`min-w-[20px] h-5 px-1 inline-flex items-center justify-center text-xs font-bold rounded-full ${SEVERITY_BADGE[group.latestSeverity ?? ''] ?? 'bg-gray-100 text-gray-600'}`}>
                  {group.count}
                </span>
              </button>
            ))}
          </div>

          {/* Latest findings — clickable rows, severity first */}
          <ul className="divide-y divide-gray-50">
            {latestSorted.slice(0, 8).map(finding => (
              <li key={finding.id}>
                <button
                  onClick={() => onSelectFinding(finding)}
                  className="w-full text-left py-2 px-1 hover:bg-primary-50/50 rounded transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${SEVERITY_DOT[finding.severity] ?? 'bg-gray-400'}`} />
                    <span className="text-sm text-gray-800 truncate flex-1">{finding.title}</span>
                    <span className="text-xs text-gray-400 flex-shrink-0">{finding.scanner}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
